/**
 * Krishna–Meera in Vrindavan — a true 3D wedding invitation.
 * Registered as exp-krishna-meera-3d-diorama.
 *
 * Hand-built 3D Krishna (flute, mor mukut, pitambar) and Meera (ivory sari
 * with red zari, ektara) stand in a golden-hour Vrindavan: kadamba tree,
 * Yamuna shimmer, marigolds, diyas, a cow and a peacock. Names and details
 * are HTML over the scene, never baked into 3D, so hosts can edit them.
 *
 * - three.js and the scene code load lazily (./scene/stage) in their own chunk.
 * - Catalog thumbnails show the static poster (META.cardPosterUrl), no WebGL.
 * - No WebGL or `?webgl=0`: poster + the same overlay.
 * - `prefers-reduced-motion` or `?motion=reduce`: one still frame, no drift.
 * - Two scenes in one template: 1 the Vrindavan grove, 2 the varmala at the
 *   Yamuna ghat (built lazily on first visit). A pill under the names moves
 *   between them; the cards and RSVP stay on screen in both.
 * - QA flags: `?chars=off` hides the characters, `?freeze=<seconds>` holds the
 *   clock (scene-local in scene 2), `?scene=2` opens on the varmala, `?cam=hold`
 *   holds the scene-2 camera still.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { applyDevPhotoFixture, isPlaceholder, resolveDioramaCopy, type DioramaCopy } from './fields';
import type { SceneId, StageApi } from './scene/stage';
import './diorama.css';

export const TEMPLATE_ID = 'exp-krishna-meera-3d-diorama';
export const POSTER_URL = `/assets/templates/${TEMPLATE_ID}/poster.webp`;
export const POSTER_VARMALA_URL = `/assets/templates/${TEMPLATE_ID}/poster-varmala.webp`;

export const SCENES: Record<SceneId, { label: string; description: string; poster: string }> = {
  1: {
    label: 'Vrindavan',
    description: 'Krishna playing the flute beside Meera with her ektara, under a kadamba tree in Vrindavan by the Yamuna at golden hour.',
    poster: POSTER_URL,
  },
  2: {
    label: 'Varmala',
    description:
      'At dusk on the Yamuna ghat, under a marigold mandap with floating diyas, Krishna and Meera exchange varmalas of marigold and rose.',
    poster: POSTER_VARMALA_URL,
  },
};

export const META: TemplateMetadata = {
  id: TEMPLATE_ID,
  name: 'Krishna–Meera in Vrindavan 3D',
  occasionType: 'WEDDING',
  description:
    'A living 3D Vrindavan at golden hour: Krishna plays his flute beside Meera with her ektara under the kadamba tree, then a second scene at the Yamuna ghat at dusk where they exchange varmalas under a marigold mandap. Your names, date, venue and RSVP sit over both scenes.',
  isPremium: true,
  tags: ['3d', 'immersive', 'krishna', 'meera', 'diorama', 'premium', 'wedding', 'vrindavan', 'varmala'],
  colorScheme: { primary: '#7a1f2b', accent: '#e8b84a', bg: '#f6e3c2' },
  cardPosterUrl: POSTER_URL,
};


function queryFlag(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

function readReducedMotion(): boolean {
  const media =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return media || queryFlag('motion') === 'reduce';
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(readReducedMotion());
    query.addEventListener?.('change', apply);
    return () => query.removeEventListener?.('change', apply);
  }, []);
  return reduced;
}

/** Text that shows the honest placeholder style when the host has not filled it in. */
function Field({ value, className, as: Tag = 'span' }: { value: string; className?: string; as?: 'span' | 'p' }) {
  const ph = isPlaceholder(value);
  return (
    <Tag className={[className, ph ? 'km-ph' : ''].filter(Boolean).join(' ')} data-placeholder={ph ? 'true' : undefined}>
      {value}
    </Tag>
  );
}

function mapsHref(venue: string): string | undefined {
  if (isPlaceholder(venue)) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`;
}

function Photos({ copy }: { copy: DioramaCopy }) {
  const photos = copy.couplePhoto ? [copy.couplePhoto] : [copy.groomPhoto, copy.bridePhoto].filter(Boolean);
  if (!photos.length) return null;
  return (
    <div className="km-photos" data-testid="km-photos">
      {photos.map((src) => (
        <img key={src} src={src} alt="" loading="lazy" />
      ))}
    </div>
  );
}

function RsvpSheet({ data, onClose }: { data: InvitationData; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        ref.current?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? [],
      ).filter((el) => !el.hasAttribute('disabled'));
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  return (
    <div className="km-sheet-backdrop" onClick={onClose} data-testid="km-rsvp-sheet">
      <div
        ref={ref}
        className="km-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="RSVP"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="km-sheet-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <RsvpForm invitationId={data.invitationId ?? data.id ?? null} isLive={isLive} />
      </div>
    </div>
  );
}

function initialScene(): SceneId {
  return queryFlag('scene') === '2' ? 2 : 1;
}

function SceneSwitch({ scene, busy, onChange }: { scene: SceneId; busy: boolean; onChange: (id: SceneId) => void }) {
  const next: SceneId = scene === 1 ? 2 : 1;
  return (
    <nav className="km-scenes" aria-label="Scenes" data-scene={scene}>
      <button
        type="button"
        className={['km-scene-pill', scene === 1 ? 'is-forward' : 'is-back'].join(' ')}
        onClick={() => onChange(next)}
        disabled={busy}
        aria-busy={busy || undefined}
        data-testid={scene === 1 ? 'km-scene-next' : 'km-scene-back'}
        aria-label={scene === 1 ? 'Go to the varmala scene' : 'Back to the Vrindavan scene'}
      >
        {scene === 1 ? (
          <>
            Varmala <span aria-hidden>→</span>
          </>
        ) : (
          <>
            <span aria-hidden>←</span> Vrindavan Garden
          </>
        )}
      </button>
      <span className="km-scene-dots" aria-hidden>
        <i className={scene === 1 ? 'is-on' : ''} />
        <i className={scene === 2 ? 'is-on' : ''} />
      </span>
    </nav>
  );
}

function Overlay({
  copy,
  data,
  scene,
  busy,
  onScene,
}: {
  copy: DioramaCopy;
  data: InvitationData;
  scene: SceneId;
  busy: boolean;
  onScene: (id: SceneId) => void;
}) {
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const map = mapsHref(copy.venue);
  const when = [copy.dateLabel, copy.timeLabel].filter(Boolean).join(' · ');
  const longNames = copy.groom.length + copy.bride.length > 26;
  return (
    <>
      <header className="km-card km-top" data-testid="km-names-card">
        <Photos copy={copy} />
        <p className="km-kicker">
          <Field value={copy.host} /> <span className="km-kicker-rest">invite you to the wedding of</span>
        </p>
        <h1 className={['km-names', longNames ? 'is-long' : ''].filter(Boolean).join(' ')}>
          <Field value={copy.groom} className="km-name" />
          <span className="km-amp" aria-hidden>
            &amp;
          </span>
          <span className="sr-only"> and </span>
          <Field value={copy.bride} className="km-name" />
        </h1>
        {scene === 1 ? (
          <p className="km-event" data-testid="km-event">
            <span className="km-deva" lang="sa">॥ शुभ विवाह ॥</span>
            <span>Wedding Ceremony</span>
          </p>
        ) : (
          <p className="km-event is-ceremony" data-testid="km-event">
            <span className="km-deva" lang="hi">॥ वरमाला ॥</span>
            <span>
              Varmala &amp; Pheras ·{' '}
              <span className="km-nowrap">
                <Field value={copy.dateLabel} />
              </span>
            </span>
          </p>
        )}
      </header>

      <section className="km-card km-bottom" data-testid="km-details-card" aria-label="Event details">
        <SceneSwitch scene={scene} busy={busy} onChange={onScene} />
        <p className="km-when" data-testid="km-when">
          {isPlaceholder(copy.dateLabel) ? <Field value={copy.dateLabel} /> : when}
        </p>
        <p className="km-venue">
          <Field value={copy.venue} />
        </p>
        {copy.message ? <p className="km-message">{copy.message}</p> : null}
        <div className="km-actions">
          {copy.rsvpHref ? (
            <a className="km-btn km-btn-primary" href={copy.rsvpHref} target="_blank" rel="noopener noreferrer" data-testid="km-rsvp">
              RSVP
            </a>
          ) : (
            <button type="button" className="km-btn km-btn-primary" onClick={() => setRsvpOpen(true)} data-testid="km-rsvp">
              RSVP
            </button>
          )}
          {map ? (
            <a className="km-btn" href={map} target="_blank" rel="noopener noreferrer">
              Directions
            </a>
          ) : null}
          {copy.customVideoUrl ? (
            <a className="km-btn" href={copy.customVideoUrl} target="_blank" rel="noopener noreferrer">
              Our story
            </a>
          ) : null}
        </div>
      </section>
      {rsvpOpen ? <RsvpSheet data={data} onClose={() => setRsvpOpen(false)} /> : null}
    </>
  );
}

export function KrishnaMeera3DDiorama({ data }: { data: InvitationData; preview?: boolean }) {
  const sceneData = useMemo(() => applyDevPhotoFixture(data), [data]);
  const copy = resolveDioramaCopy(sceneData);
  const reduced = usePrefersReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<'pending' | 'poster' | 'live'>('pending');
  const [no3d, setNo3d] = useState(false);
  const [ready, setReady] = useState(false);
  const [scene, setScene] = useState<SceneId>(initialScene);
  const [busy, setBusy] = useState(false);
  const [veil, setVeil] = useState(false);
  const stageRef = useRef<StageApi | null>(null);
  const sceneRef = useRef<SceneId>(scene);

  useLayoutEffect(() => {
    setMode(rootRef.current?.closest('[data-template-thumbnail]') ? 'poster' : 'live');
  }, []);

  useEffect(() => {
    if (mode !== 'live') return;
    if (queryFlag('webgl') === '0' || !hasWebGL()) {
      setNo3d(true);
      return;
    }
    let alive = true;
    let stage: StageApi | null = null;
    setReady(false);
    import('./scene/stage')
      .then((mod) => {
        if (!alive || !canvasRef.current) return;
        const freeze = Number(queryFlag('freeze'));
        stage = mod.createStage(canvasRef.current, {
          reduced,
          characters: queryFlag('chars') !== 'off',
          freezeAt: Number.isFinite(freeze) && queryFlag('freeze') !== null ? freeze : undefined,
          scene: sceneRef.current,
          holdCamera: queryFlag('cam') === 'hold',
          onReady: () => alive && setReady(true),
        });
        stageRef.current = stage;
        if (queryFlag('qa') === '1') (window as unknown as { __kmStage?: StageApi }).__kmStage = stage;
      })
      .catch(() => {
        if (alive) setNo3d(true);
      });
    return () => {
      alive = false;
      stageRef.current = null;
      stage?.dispose();
    };
  }, [mode, reduced]);

  // Scene switch: a short warm veil hides the cut while scene 2 builds on
  // first visit; reduced motion swaps instantly.
  const changeScene = async (id: SceneId) => {
    if (id === sceneRef.current || busy) return;
    sceneRef.current = id;
    const stage = stageRef.current;
    if (!stage) {
      setScene(id);
      return;
    }
    setBusy(true);
    if (!reduced) {
      setVeil(true);
      await new Promise((r) => setTimeout(r, 420));
    }
    try {
      await stage.setScene(id);
    } catch {
      setNo3d(true);
    }
    setScene(id);
    setVeil(false);
    setBusy(false);
  };

  const state = mode === 'poster' ? 'poster' : no3d ? 'fallback' : ready ? 'live' : 'loading';
  return (
    <div
      ref={rootRef}
      data-testid="krishna-meera-diorama"
      data-state={state}
      data-reduced-motion={reduced ? 'true' : 'false'}
      data-scene={scene}
      className="km-diorama"
    >
      {mode === 'poster' ? (
        <div className="km-poster" data-testid="km-poster" aria-hidden style={{ backgroundImage: `url(${POSTER_URL})` }} />
      ) : null}
      {mode === 'live' ? (
        <>
          <div
            className={['km-poster', 'km-poster-under', ready ? 'is-hidden' : ''].filter(Boolean).join(' ')}
            style={{ backgroundImage: `url(${SCENES[scene].poster})` }}
            data-testid={no3d ? 'km-fallback' : undefined}
            aria-hidden
          />
          {!no3d ? <canvas ref={canvasRef} className="km-canvas" aria-hidden="true" data-testid="km-canvas" /> : null}
          <div className={['km-veil', veil ? 'is-on' : ''].filter(Boolean).join(' ')} aria-hidden data-testid="km-veil" />
          <p className="sr-only" aria-live="polite" data-testid="km-scene-description">
            {SCENES[scene].description}
          </p>
          <Overlay copy={copy} data={sceneData} scene={scene} busy={busy} onScene={(id) => void changeScene(id)} />
          <div className="km-partner-safe" aria-hidden />
        </>
      ) : null}
    </div>
  );
}

registry.register({ metadata: META, component: KrishnaMeera3DDiorama });
